import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { workflowService, type WorkflowSummary } from '../services/workflowService';
import { Badge } from '../components/ui/Badge';

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-800',
};

/**
 * Workflows Page Component
 *
 *
 * Page for managing AI workflows and automation with drag-and-drop builder
 *
 *
 * @returns {JSX.Element} Workflows page component
 */
export const Workflows: React.FC = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const res = await workflowService.list({ limit: 100 });
      const items = (res as any)?.items ?? (Array.isArray(res) ? res : []);
      setWorkflows(items);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this workflow? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await workflowService.delete(id);
      toast.success('Workflow deleted');
      loadWorkflows();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete workflow');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Page contentClassName="py-6">
      <PageHeader
        className="py-0"
        title="AI Workflows"
        subtitle="Design and manage your AI automation workflows here."
        aside={
          <Button onClick={() => navigate('/workflows/new')}>
            Create New Workflow
          </Button>
        }
      />

      <Section className="py-6">
        {loading ? (
          <Card className="px-6 py-12 text-center">
            <p className="text-gray-600">Loading workflows...</p>
          </Card>
        ) : workflows.length === 0 ? (
          <Card className="px-6 py-12 text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">No workflows yet</h3>
            <p className="mb-6 text-gray-600">Create your first AI workflow to get started.</p>
            <Button onClick={() => navigate('/workflows/new')}>
              Create Workflow
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {workflows.map((workflow) => (
              <Card
                key={workflow.id}
                hover
                className="flex min-w-0 flex-col border-gray-100"
              >
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-lg font-semibold text-gray-900">{workflow.name}</h3>
                    <p className="mt-2 text-sm text-gray-600">
                      {workflow.description || 'No description'}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${
                      STATUS_BADGE[workflow.status] ?? 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {workflow.status}
                  </span>
                </div>

                <div className="mb-4 space-y-2 text-sm text-gray-500">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      Updated {new Date(workflow.updatedAt).toLocaleDateString()}
                    </span>
                    <span className="shrink-0">
                      {workflow.nodeCount} node{workflow.nodeCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  {workflow.agentNames.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {workflow.agentNames.map((agent) => (
                        <Badge key={agent} className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-700">
                          {agent}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="mt-auto flex gap-2">
                  <Button
                    onClick={() => navigate(`/workflows/${workflow.id}/edit`)}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="flex-1" disabled>
                    Duplicate
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={deletingId === workflow.id}
                    onClick={() => handleDelete(workflow.id)}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>
    </Page>
  );
};
